import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Header } from "@/components/Header";
import { adminLogin, adminGetBookings, adminCancelBooking, adminGetStats, adminUpdateBooking, adminSendReminder, adminGetWaitlist, adminRemoveWaitlist } from "@/lib/api";
import { formatDateShort, formatDateEU } from "@/lib/format";
import { toast } from "sonner";
import { Lock, LogOut, Trash2, RefreshCw, StickyNote, FileDigit, Calendar as CalendarIcon, Send, Bell, Check, Printer, FileText, Download } from "lucide-react";

const TOKEN_KEY = "ice_admin_token";

const STATUSES = ["submitted", "in-progress", "confirmed", "invoice sent", "paid", "cancelled"];

const PRICE_PER_CHILD = 14;
const formatEuro = (n) => `€${Number(n || 0).toLocaleString("en-IE")}`;

const STATUS_STYLES = {
  submitted: "bg-slate-100 text-slate-900 border-slate-900",
  "in-progress": "bg-amber-100 text-amber-900 border-amber-900",
  confirmed: "bg-sky-500 text-white border-slate-900",
  "invoice sent": "bg-indigo-100 text-indigo-900 border-indigo-900",
  paid: "bg-emerald-500 text-white border-slate-900",
  cancelled: "bg-slate-900 text-white border-slate-900",
};

const StatusBadge = ({ status }) => {
  const s = status || "submitted";
  const cls = STATUS_STYLES[s] || STATUS_STYLES.submitted;
  return (
    <span
      className={`inline-block border-2 px-2 py-1 font-mono-tight text-[10px] uppercase tracking-widest whitespace-nowrap ${cls}`}
      data-testid={`status-badge-${s}`}
    >
      {s}
    </span>
  );
};

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [searchParams, setSearchParams] = useSearchParams();
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("");
  const [showCancelled, setShowCancelled] = useState(false);
  const [expandedNotes, setExpandedNotes] = useState({});
  const [drafts, setDrafts] = useState({});
  const [savingNotes, setSavingNotes] = useState({});
  const [waitlist, setWaitlist] = useState([]);
  const [config, setConfig] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const toggleSelected = (id) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/config`).then((r) => r.json()).then(setConfig).catch(() => {});
  }, []);

  const toggleNotes = (b) => {
    setExpandedNotes((prev) => {
      const next = { ...prev, [b.id]: !prev[b.id] };
      if (next[b.id]) {
        setDrafts((d) => ({
          ...d,
          [b.id]: {
            admin_notes: b.admin_notes || "",
            status: b.status || "submitted",
            po_number: b.po_number || "",
            date: b.date,
            time: b.time,
            school_name: b.school_name || "",
            school_address: b.school_address || "",
            teacher_name: b.teacher_name || "",
            email: b.email || "",
            phone: b.phone || "",
            county: b.county || "",
            payment_method: b.payment_method || "",
            group_size: b.group_size,
            age_group: b.age_group || "",
            notes: b.notes || "",
          },
        }));
      }
      return next;
    });
  };

  const patchDraft = (id, patch) => setDrafts((d) => ({ ...d, [id]: { ...d[id], ...patch } }));

  const saveDraft = async (id) => {
    try {
      setSavingNotes((s) => ({ ...s, [id]: true }));
      const draft = drafts[id] || {};
      const updated = await adminUpdateBooking(token, id, draft);
      setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, ...updated } : b)));
      toast.success("Saved");
    } catch (err) {
      toast.error("Failed to save");
    } finally {
      setSavingNotes((s) => ({ ...s, [id]: false }));
    }
  };


  const load = async (t) => {
    try {
      setLoading(true);
      const [b, s, w] = await Promise.all([adminGetBookings(t), adminGetStats(t), adminGetWaitlist(t)]);
      setBookings(b);
      setStats(s);
      setWaitlist(w);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired");
        setToken(null);
        localStorage.removeItem(TOKEN_KEY);
      } else {
        toast.error("Failed to load bookings");
      }
    } finally {
      setLoading(false);
    }
  };

  const removeWaitlistEntry = async (id) => {
    if (!window.confirm("Remove this waiting-list entry?")) return;
    try {
      await adminRemoveWaitlist(token, id);
      setWaitlist((prev) => prev.filter((w) => w.id !== id));
      toast.success("Removed");
    } catch (err) {
      toast.error("Failed to remove");
    }
  };

  const exportCsv = async () => {
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/bookings/export.csv`;
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error("Export failed");
      const blob = await resp.blob();
      const dl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dl;
      const cd = resp.headers.get("content-disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : "clonmel-on-ice-bookings.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(dl);
      toast.success("Downloaded CSV");
    } catch (err) {
      toast.error("Export failed");
    }
  };

  useEffect(() => {
    if (token) load(token);
  }, [token]);

  // Auto-open edit panel when ?edit=<id> query param is present (e.g. from calendar view)
  useEffect(() => {
    const editId = searchParams.get("edit");
    if (!editId || bookings.length === 0) return;
    const b = bookings.find((x) => x.id === editId);
    if (!b) return;
    if (!expandedNotes[editId]) {
      toggleNotes(b);
    }
    // Give React a beat to render then scroll into view
    setTimeout(() => {
      const row = document.querySelector(`[data-testid="admin-row-${b.reference}"]`);
      if (row) row.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
    // Clear the query param so refreshes don't re-trigger
    searchParams.delete("edit");
    setSearchParams(searchParams, { replace: true });
  }, [bookings, searchParams]);

  const doLogin = async (e) => {
    e.preventDefault();
    try {
      setLoggingIn(true);
      const res = await adminLogin(password);
      localStorage.setItem(TOKEN_KEY, res.token);
      setToken(res.token);
      toast.success("Welcome, admin");
      setPassword("");
    } catch (err) {
      toast.error("Invalid password");
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setBookings([]);
    setStats(null);
  };

  const cancel = async (id, ref) => {
    if (!window.confirm(`Cancel booking ${ref}? This cannot be undone.`)) return;
    try {
      await adminCancelBooking(token, id);
      toast.success("Booking cancelled");
      load(token);
    } catch (err) {
      toast.error("Failed to cancel");
    }
  };

  const sendReminder = async (id, ref) => {
    if (!window.confirm(`Send reminder email for ${ref}?`)) return;
    try {
      const res = await adminSendReminder(token, id);
      toast.success(`Reminder sent (id ${res.email_id?.slice(-6) || "ok"})`);
      setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, reminder_sent_at: new Date().toISOString() } : b)));
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to send reminder";
      toast.error(typeof msg === "string" ? msg : "Failed to send reminder");
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="max-w-md mx-auto px-4 md:px-8 py-20">
          <div className="border-2 border-slate-900 bg-white brutal-shadow-lg p-8">
            <div className="w-12 h-12 bg-slate-900 border-2 border-slate-900 flex items-center justify-center mb-5">
              <Lock className="w-6 h-6 text-white" strokeWidth={2.5} />
            </div>
            <div className="font-mono-tight text-xs uppercase tracking-[0.25em] text-slate-500 mb-1">
              Restricted
            </div>
            <h1 className="font-display text-3xl uppercase mb-6">Admin login</h1>
            <form onSubmit={doLogin} className="space-y-4" data-testid="admin-login-form">
              <div>
                <div className="font-mono-tight text-[11px] uppercase tracking-[0.2em] text-slate-500 mb-2">
                  Password
                </div>
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-white border-2 border-slate-900 p-3 font-mono-tight focus:outline-none focus:border-sky-500"
                  data-testid="admin-password-input"
                />
              </div>
              <button
                type="submit"
                disabled={loggingIn}
                className="w-full bg-sky-500 text-white font-display uppercase tracking-wide border-2 border-slate-900 brutal-shadow brutal-hover px-6 py-3 disabled:opacity-60"
                data-testid="admin-login-button"
              >
                {loggingIn ? "Signing in…" : "Sign in"}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  const filtered = bookings
    .filter((b) => (showCancelled ? true : !b.cancelled))
    .filter((b) => {
      if (!filter) return true;
      const f = filter.toLowerCase();
      return (
        b.school_name.toLowerCase().includes(f) ||
        b.teacher_name.toLowerCase().includes(f) ||
        b.email.toLowerCase().includes(f) ||
        b.reference.toLowerCase().includes(f) ||
        (b.county || "").toLowerCase().includes(f) ||
        b.date.includes(f)
      );
    });

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <div className="max-w-[1600px] mx-auto px-4 md:px-6 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="font-mono-tight text-xs uppercase tracking-[0.25em] text-slate-500 mb-1">
              Control room
            </div>
            <h1 className="font-display text-4xl md:text-5xl uppercase">Admin dashboard</h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={exportCsv}
              className="bg-white text-slate-900 font-mono-tight text-xs uppercase tracking-widest border-2 border-slate-900 brutal-shadow brutal-hover px-4 py-2 flex items-center gap-2"
              data-testid="admin-export-csv"
            >
              <Download className="w-3.5 h-3.5" strokeWidth={2.5} /> Export CSV
            </button>
            <Link
              to="/admin/day"
              className="bg-white text-slate-900 font-mono-tight text-xs uppercase tracking-widest border-2 border-slate-900 brutal-shadow brutal-hover px-4 py-2 flex items-center gap-2"
              data-testid="admin-open-daysummary"
            >
              <Printer className="w-3.5 h-3.5" strokeWidth={2.5} /> Day summary
            </Link>
            <Link
              to="/admin/calendar"
              className="bg-sky-500 text-white font-mono-tight text-xs uppercase tracking-widest border-2 border-slate-900 brutal-shadow brutal-hover px-4 py-2 flex items-center gap-2"
              data-testid="admin-open-calendar"
            >
              <CalendarIcon className="w-3.5 h-3.5" strokeWidth={2.5} /> Calendar view
            </Link>
            <button
              onClick={() => load(token)}
              className="bg-white text-slate-900 font-mono-tight text-xs uppercase tracking-widest border-2 border-slate-900 brutal-shadow brutal-hover px-4 py-2 flex items-center gap-2"
              data-testid="admin-refresh"
            >
              <RefreshCw className="w-3.5 h-3.5" strokeWidth={2.5} /> Refresh
            </button>
            <button
              onClick={logout}
              className="bg-slate-900 text-white font-mono-tight text-xs uppercase tracking-widest border-2 border-slate-900 brutal-shadow brutal-hover px-4 py-2 flex items-center gap-2"
              data-testid="admin-logout"
            >
              <LogOut className="w-3.5 h-3.5" strokeWidth={2.5} /> Logout
            </button>
          </div>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <StatCard label="Total slots" value={stats.total_slots} />
            <StatCard label="Booked" value={stats.booked} accent />
            <StatCard label="Available" value={stats.available} />
            <StatCard label="Cancelled" value={stats.cancelled} />
            <StatCard
              label="Est. revenue (active)"
              value={formatEuro(
                bookings
                  .filter((b) => !b.cancelled && b.status !== "cancelled")
                  .reduce((sum, b) => sum + (b.group_size || 0) * PRICE_PER_CHILD, 0)
              )}
            />
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3 mb-4">
          <input
            placeholder="Search school, teacher, email, date, ref…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="flex-1 min-w-[240px] bg-white border-2 border-slate-900 p-3 font-mono-tight text-sm focus:outline-none focus:border-sky-500"
            data-testid="admin-search"
          />
          <label className="flex items-center gap-2 font-mono-tight text-xs uppercase tracking-widest cursor-pointer">
            <input
              type="checkbox"
              checked={showCancelled}
              onChange={(e) => setShowCancelled(e.target.checked)}
              className="w-4 h-4 accent-sky-500"
              data-testid="admin-show-cancelled"
            />
            Show cancelled
          </label>
          {selectedIds.size > 1 && (
            <Link
              to={`/admin/invoice/multi?ids=${Array.from(selectedIds).join(",")}`}
              className="bg-indigo-500 text-white font-display uppercase tracking-wide border-2 border-slate-900 brutal-shadow brutal-hover px-4 py-2 flex items-center gap-2 text-sm"
              data-testid="admin-combine-invoice"
            >
              <FileText className="w-4 h-4" strokeWidth={2.5} /> Combine invoice ({selectedIds.size})
            </Link>
          )}
          {selectedIds.size > 0 && (
            <button
              onClick={() => setSelectedIds(new Set())}
              className="bg-white text-slate-900 font-mono-tight text-xs uppercase tracking-widest border-2 border-slate-900 brutal-shadow brutal-hover px-3 py-2"
              data-testid="admin-clear-selection"
            >
              Clear selection
            </button>
          )}
        </div>

        <div className="border-2 border-slate-900 bg-white brutal-shadow" data-testid="admin-bookings-table">
          <table className="w-full text-xs">
            <thead className="bg-slate-900 text-white font-mono-tight uppercase text-[10px] tracking-widest">
              <tr>
                <Th><span className="sr-only">Select</span></Th>
                <Th>Status</Th>
                <Th>Ref</Th>
                <Th>When</Th>
                <Th>School</Th>
                <Th>Teacher · contact</Th>
                <Th>County</Th>
                <Th>Group</Th>
                <Th>Cost</Th>
                <Th>Age</Th>
                <Th>PO #</Th>
                <Th>Action</Th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={12} className="text-center p-12 font-mono-tight uppercase tracking-widest">
                    Loading…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={12} className="text-center p-12 font-mono-tight uppercase tracking-widest text-slate-500">
                    No bookings
                  </td>
                </tr>
              ) : (
                filtered.map((b) => (
                  <React.Fragment key={b.id}>
                  <tr
                    className={`border-t-2 border-slate-900 ${b.cancelled ? "opacity-60" : ""} ${selectedIds.has(b.id) ? "bg-sky-50" : ""}`}
                    data-testid={`admin-row-${b.reference}`}
                  >
                    <Td>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(b.id)}
                        onChange={() => toggleSelected(b.id)}
                        disabled={b.cancelled}
                        className="w-4 h-4 accent-sky-500 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid={`admin-select-${b.reference}`}
                        aria-label={`Select booking ${b.reference}`}
                      />
                    </Td>
                    <Td>
                      <StatusBadge status={b.status} />
                    </Td>
                    <Td mono>{b.reference}</Td>
                    <Td mono>
                      <div className="whitespace-nowrap">{formatDateShort(b.date)}</div>
                      <div className="text-slate-500 text-[11px]">{b.time}</div>
                    </Td>
                    <Td>
                      <div className="font-medium leading-tight">{b.school_name}</div>
                      <div className="text-slate-500 text-[11px] leading-tight">{b.school_address || ""}</div>
                    </Td>
                    <Td>
                      <div className="leading-tight">{b.teacher_name}</div>
                      <div className="text-slate-500 text-[11px] leading-tight break-all">{b.email}</div>
                      <div className="text-slate-500 text-[11px] leading-tight">{b.phone}</div>
                    </Td>
                    <Td>{b.county || "—"}</Td>
                    <Td mono className="text-center">{b.group_size}</Td>
                    <Td mono>
                      <span className="font-bold text-slate-900 whitespace-nowrap" data-testid={`admin-cost-${b.reference}`}>
                        {formatEuro(b.group_size * PRICE_PER_CHILD)}
                      </span>
                    </Td>
                    <Td>
                      <div className="text-[11px] leading-tight max-w-[120px]">{b.age_group}</div>
                    </Td>
                    <Td mono className="text-[11px]">{b.po_number || "—"}</Td>
                    <Td>
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => toggleNotes(b)}
                          className={`border-2 border-slate-900 p-1.5 transition-colors relative ${
                            (b.admin_notes || b.po_number) ? "bg-sky-500 text-white hover:bg-sky-600" : "bg-white text-slate-900 hover:bg-sky-100"
                          }`}
                          title="Edit / admin details"
                          data-testid={`admin-notes-toggle-${b.reference}`}
                        >
                          <StickyNote className="w-3 h-3" strokeWidth={2.5} />
                          {(b.admin_notes || b.po_number) && (
                            <span className="absolute -top-1 -right-1 w-2 h-2 bg-slate-900 border border-white rounded-full" />
                          )}
                        </button>
                        {b.cancelled ? (
                          <span className="font-mono-tight text-[10px] uppercase text-slate-400 whitespace-nowrap">Cancelled</span>
                        ) : (
                          <>
                            <Link
                              to={`/admin/invoice/${b.id}`}
                              className={`border-2 border-slate-900 p-1.5 transition-colors ${
                                b.invoice_sent_at ? "bg-indigo-500 text-white hover:bg-indigo-600" : "bg-white text-slate-900 hover:bg-sky-500 hover:text-white"
                              }`}
                              data-testid={`admin-invoice-${b.reference}`}
                              title={b.invoice_sent_at ? "Invoice sent · click to view/resend" : "View / email invoice"}
                            >
                              <FileText className="w-3 h-3" strokeWidth={2.5} />
                            </Link>
                            <button
                              onClick={() => sendReminder(b.id, b.reference)}
                              className={`border-2 border-slate-900 p-1.5 transition-colors ${
                                b.reminder_sent_at ? "bg-emerald-500 text-white hover:bg-emerald-600" : "bg-white text-slate-900 hover:bg-sky-500 hover:text-white"
                              }`}
                              data-testid={`admin-reminder-${b.reference}`}
                              title={b.reminder_sent_at ? `Reminder sent · click to resend` : "Send reminder email"}
                            >
                              <Send className="w-3 h-3" strokeWidth={2.5} />
                            </button>
                            <button
                              onClick={() => cancel(b.id, b.reference)}
                              className="bg-white text-slate-900 border-2 border-slate-900 hover:bg-sky-500 hover:text-white p-1.5 transition-colors"
                              data-testid={`cancel-${b.reference}`}
                              title="Cancel booking"
                            >
                              <Trash2 className="w-3 h-3" strokeWidth={2.5} />
                            </button>
                          </>
                        )}
                      </div>
                    </Td>
                  </tr>
                  {expandedNotes[b.id] && (
                    <tr className="border-t-2 border-slate-900 bg-sky-50" data-testid={`admin-notes-row-${b.reference}`}>
                      <td colSpan={12} className="p-5">
                        <div className="flex items-center gap-2 mb-4">
                          <StickyNote className="w-3.5 h-3.5 text-sky-600" strokeWidth={2.5} />
                          <div className="font-mono-tight text-[11px] uppercase tracking-[0.25em] text-slate-500">
                            Edit booking · {b.reference}
                          </div>
                          <div className="ml-auto font-mono-tight text-[10px] uppercase tracking-widest text-slate-400">
                            Admin only — teachers do not see admin fields
                          </div>
                        </div>

                        {/* Admin-only section */}
                        <div className="border-2 border-slate-900 bg-white p-4 mb-4">
                          <div className="font-mono-tight text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-3">Admin fields</div>
                          <div className="grid md:grid-cols-3 gap-4 mb-3">
                            <EditField label="Status">
                              <select
                                value={drafts[b.id]?.status ?? "submitted"}
                                onChange={(e) => patchDraft(b.id, { status: e.target.value })}
                                className="w-full bg-white border-2 border-slate-900 p-2.5 font-mono-tight text-sm focus:outline-none focus:border-sky-500"
                                data-testid={`admin-status-select-${b.reference}`}
                              >
                                {STATUSES.map((s) => (<option key={s} value={s}>{s}</option>))}
                              </select>
                            </EditField>
                            <EditField label="PO number">
                              <input
                                value={drafts[b.id]?.po_number ?? ""}
                                onChange={(e) => patchDraft(b.id, { po_number: e.target.value })}
                                placeholder="e.g. PO-2026-0123"
                                className="w-full bg-white border-2 border-slate-900 p-2.5 font-mono-tight text-sm focus:outline-none focus:border-sky-500"
                                data-testid={`admin-po-input-${b.reference}`}
                              />
                            </EditField>
                            <EditField label="Reference (read-only)">
                              <input
                                value={b.reference}
                                readOnly
                                className="w-full bg-slate-100 border-2 border-slate-900 p-2.5 font-mono-tight text-sm text-slate-500 cursor-not-allowed"
                              />
                            </EditField>
                          </div>
                          <EditField label="Admin notes (private)">
                            <textarea
                              rows={3}
                              placeholder="Conversation log, payment status, on-day arrangements…"
                              value={drafts[b.id]?.admin_notes ?? ""}
                              onChange={(e) => patchDraft(b.id, { admin_notes: e.target.value })}
                              className="w-full bg-white border-2 border-slate-900 p-2.5 font-mono-tight text-sm focus:outline-none focus:border-sky-500"
                              data-testid={`admin-notes-textarea-${b.reference}`}
                            />
                          </EditField>
                        </div>

                        {/* Booking fields */}
                        <div className="border-2 border-slate-900 bg-white p-4 mb-4">
                          <div className="font-mono-tight text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-3">Booking fields</div>
                          <div className="grid md:grid-cols-3 gap-4">
                            <EditField label="Date">
                              <select
                                value={drafts[b.id]?.date ?? b.date}
                                onChange={(e) => patchDraft(b.id, { date: e.target.value })}
                                className="w-full bg-white border-2 border-slate-900 p-2.5 font-mono-tight text-sm focus:outline-none focus:border-sky-500"
                                data-testid={`edit-date-${b.reference}`}
                              >
                                {(config?.valid_dates || [b.date]).map((d) => (
                                  <option key={d} value={d}>{formatDateEU(d)}</option>
                                ))}
                              </select>
                            </EditField>
                            <EditField label="Time">
                              <select
                                value={drafts[b.id]?.time ?? b.time}
                                onChange={(e) => patchDraft(b.id, { time: e.target.value })}
                                className="w-full bg-white border-2 border-slate-900 p-2.5 font-mono-tight text-sm focus:outline-none focus:border-sky-500"
                                data-testid={`edit-time-${b.reference}`}
                              >
                                {(config?.session_times || [b.time]).map((t) => (
                                  <option key={t} value={t}>{t}</option>
                                ))}
                              </select>
                            </EditField>
                            <EditField label="Group size (admin override)">
                              <input
                                type="number"
                                min="1"
                                value={drafts[b.id]?.group_size ?? b.group_size}
                                onChange={(e) => patchDraft(b.id, { group_size: parseInt(e.target.value, 10) || "" })}
                                className="w-full bg-white border-2 border-slate-900 p-2.5 font-mono-tight text-sm focus:outline-none focus:border-sky-500"
                                data-testid={`edit-group-size-${b.reference}`}
                              />
                            </EditField>
                            <EditField label="School name">
                              <input
                                value={drafts[b.id]?.school_name ?? ""}
                                onChange={(e) => patchDraft(b.id, { school_name: e.target.value })}
                                className="w-full bg-white border-2 border-slate-900 p-2.5 font-mono-tight text-sm focus:outline-none focus:border-sky-500"
                                data-testid={`edit-school-name-${b.reference}`}
                              />
                            </EditField>
                            <EditField label="Teacher name">
                              <input
                                value={drafts[b.id]?.teacher_name ?? ""}
                                onChange={(e) => patchDraft(b.id, { teacher_name: e.target.value })}
                                className="w-full bg-white border-2 border-slate-900 p-2.5 font-mono-tight text-sm focus:outline-none focus:border-sky-500"
                                data-testid={`edit-teacher-name-${b.reference}`}
                              />
                            </EditField>
                            <EditField label="County">
                              <select
                                value={drafts[b.id]?.county ?? ""}
                                onChange={(e) => patchDraft(b.id, { county: e.target.value })}
                                className="w-full bg-white border-2 border-slate-900 p-2.5 font-mono-tight text-sm focus:outline-none focus:border-sky-500"
                                data-testid={`edit-county-${b.reference}`}
                              >
                                {(config?.counties || [b.county]).map((c) => (
                                  <option key={c} value={c}>{c}</option>
                                ))}
                              </select>
                            </EditField>
                            <EditField label="Email">
                              <input
                                type="email"
                                value={drafts[b.id]?.email ?? ""}
                                onChange={(e) => patchDraft(b.id, { email: e.target.value })}
                                className="w-full bg-white border-2 border-slate-900 p-2.5 font-mono-tight text-sm focus:outline-none focus:border-sky-500"
                                data-testid={`edit-email-${b.reference}`}
                              />
                            </EditField>
                            <EditField label="Phone">
                              <input
                                value={drafts[b.id]?.phone ?? ""}
                                onChange={(e) => patchDraft(b.id, { phone: e.target.value })}
                                className="w-full bg-white border-2 border-slate-900 p-2.5 font-mono-tight text-sm focus:outline-none focus:border-sky-500"
                                data-testid={`edit-phone-${b.reference}`}
                              />
                            </EditField>
                            <EditField label="Payment method">
                              <select
                                value={drafts[b.id]?.payment_method ?? ""}
                                onChange={(e) => patchDraft(b.id, { payment_method: e.target.value })}
                                className="w-full bg-white border-2 border-slate-900 p-2.5 font-mono-tight text-sm focus:outline-none focus:border-sky-500"
                                data-testid={`edit-payment-${b.reference}`}
                              >
                                {(config?.payment_methods || [b.payment_method]).map((p) => (
                                  <option key={p} value={p}>{p}</option>
                                ))}
                              </select>
                            </EditField>
                            <EditField label="Age group" className="md:col-span-2">
                              <select
                                value={drafts[b.id]?.age_group ?? ""}
                                onChange={(e) => patchDraft(b.id, { age_group: e.target.value })}
                                className="w-full bg-white border-2 border-slate-900 p-2.5 font-mono-tight text-sm focus:outline-none focus:border-sky-500"
                                data-testid={`edit-age-group-${b.reference}`}
                              >
                                <option value="Primary - 1st to 3rd Class">Primary - 1st to 3rd Class</option>
                                <option value="Primary - 4th to 6th Class">Primary - 4th to 6th Class</option>
                                <option value="Post Primary - 1st Years to 3rd Years">Post Primary - 1st Years to 3rd Years</option>
                                <option value="Post Primary - 4th Years to 6th Years">Post Primary - 4th Years to 6th Years</option>
                              </select>
                            </EditField>
                            <EditField label="School address" className="md:col-span-3">
                              <input
                                value={drafts[b.id]?.school_address ?? ""}
                                onChange={(e) => patchDraft(b.id, { school_address: e.target.value })}
                                placeholder="Street, town, Eircode"
                                className="w-full bg-white border-2 border-slate-900 p-2.5 font-mono-tight text-sm focus:outline-none focus:border-sky-500"
                                data-testid={`edit-school-address-${b.reference}`}
                              />
                            </EditField>
                            <EditField label="Teacher notes" className="md:col-span-3">
                              <textarea
                                rows={2}
                                value={drafts[b.id]?.notes ?? ""}
                                onChange={(e) => patchDraft(b.id, { notes: e.target.value })}
                                className="w-full bg-white border-2 border-slate-900 p-2.5 font-mono-tight text-sm focus:outline-none focus:border-sky-500"
                                data-testid={`edit-notes-${b.reference}`}
                              />
                            </EditField>
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => saveDraft(b.id)}
                            disabled={savingNotes[b.id]}
                            className="bg-sky-500 text-white font-display uppercase tracking-wide border-2 border-slate-900 brutal-shadow brutal-hover px-5 py-2.5 text-sm disabled:opacity-60"
                            data-testid={`admin-notes-save-${b.reference}`}
                          >
                            {savingNotes[b.id] ? "Saving…" : "Save changes"}
                          </button>
                          <button
                            onClick={() => toggleNotes(b)}
                            className="bg-white text-slate-900 font-mono-tight text-xs uppercase tracking-widest border-2 border-slate-900 brutal-shadow brutal-hover px-4 py-2.5"
                          >
                            Close
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* WAITING LIST */}
        <div className="mt-10">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-3">
            <div>
              <div className="font-mono-tight text-xs uppercase tracking-[0.25em] text-slate-500 mb-1 flex items-center gap-2">
                <Bell className="w-3.5 h-3.5" strokeWidth={2.5} /> Waiting list
              </div>
              <h2 className="font-display text-2xl md:text-3xl uppercase">
                {waitlist.length} {waitlist.length === 1 ? "person" : "people"} waiting
              </h2>
            </div>
            <div className="font-mono-tight text-[10px] uppercase tracking-widest text-slate-400">
              Auto-emailed the moment a slot is cancelled
            </div>
          </div>

          <div className="border-2 border-slate-900 bg-white brutal-shadow" data-testid="admin-waitlist-table">
            <table className="w-full text-xs">
              <thead className="bg-slate-900 text-white font-mono-tight uppercase text-[10px] tracking-widest">
                <tr>
                  <Th>Slot</Th>
                  <Th>School · teacher</Th>
                  <Th>Contact</Th>
                  <Th>Requested</Th>
                  <Th>Notified?</Th>
                  <Th>Action</Th>
                </tr>
              </thead>
              <tbody>
                {waitlist.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center p-10 font-mono-tight uppercase tracking-widest text-slate-500">
                      Nobody is waiting
                    </td>
                  </tr>
                ) : (
                  waitlist.map((w) => (
                    <tr key={w.id} className="border-t-2 border-slate-900" data-testid={`waitlist-row-${w.id}`}>
                      <Td mono>
                        <div className="whitespace-nowrap">{formatDateShort(w.date)}</div>
                        <div className="text-slate-500 text-[11px]">{w.time}</div>
                      </Td>
                      <Td>
                        <div className="font-medium leading-tight">{w.school_name}</div>
                        <div className="text-slate-500 text-[11px] leading-tight">{w.teacher_name}</div>
                      </Td>
                      <Td>
                        <div className="leading-tight break-all">{w.email}</div>
                        <div className="text-slate-500 text-[11px] leading-tight">{w.phone}</div>
                      </Td>
                      <Td mono className="text-[11px] text-slate-500">
                        {new Date(w.created_at).toLocaleDateString("en-IE", { day: "2-digit", month: "short" })}
                      </Td>
                      <Td>
                        {w.notified_at ? (
                          <span className="inline-flex items-center gap-1 bg-emerald-500 text-white border-2 border-slate-900 px-2 py-0.5 font-mono-tight text-[10px] uppercase tracking-widest">
                            <Check className="w-3 h-3" strokeWidth={3} /> Notified
                          </span>
                        ) : (
                          <span className="bg-slate-100 text-slate-700 border-2 border-slate-900 px-2 py-0.5 font-mono-tight text-[10px] uppercase tracking-widest">
                            Pending
                          </span>
                        )}
                      </Td>
                      <Td>
                        <button
                          onClick={() => removeWaitlistEntry(w.id)}
                          className="bg-white text-slate-900 border-2 border-slate-900 hover:bg-sky-500 hover:text-white p-1.5 transition-colors"
                          data-testid={`waitlist-remove-${w.id}`}
                          title="Remove from waiting list"
                        >
                          <Trash2 className="w-3 h-3" strokeWidth={2.5} />
                        </button>
                      </Td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

const Th = ({ children }) => <th className="text-left px-2.5 py-2.5 whitespace-nowrap">{children}</th>;
const Td = ({ children, mono, className = "" }) => (
  <td className={`px-2.5 py-2.5 align-top ${mono ? "font-mono-tight" : ""} ${className}`}>{children}</td>
);

const EditField = ({ label, className = "", children }) => (
  <label className={`block ${className}`}>
    <div className="font-mono-tight text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1.5">{label}</div>
    {children}
  </label>
);

const StatCard = ({ label, value, accent }) => (
  <div className={`border-2 border-slate-900 p-5 brutal-shadow ${accent ? "bg-sky-500 text-white" : "bg-white"}`}>
    <div className={`font-mono-tight text-[10px] uppercase tracking-[0.25em] ${accent ? "text-white/80" : "text-slate-500"} mb-2`}>
      {label}
    </div>
    <div className="font-display text-4xl leading-none">{value}</div>
  </div>
);
